import React, { useMemo } from 'react';
import { Counter } from '../CounterBadge';
import { Card as CardWrapper } from '../Card';
import Card from './Card';
import Loader from 'components/loaderComponent';
import PaginateBlock from 'components/PaginateBlock';
import { useLocation } from 'react-router-dom';
import { useSaveUrlFilter } from 'hooks/useSaveUrlFilter';
import { useAppointmentRequests } from './useAppointmentRequests';
import { GetPatientOutput } from '../../../../graphql/generated/hasura';
import { twMerge } from 'tailwind-merge';

const defaultFilter = {
  limit: 3,
  offset: 0,
};

const offsetParams = 'myRequestOffset';

const AppointmentRequests = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const offset = queryParams.get(offsetParams);

  const [filter, setFilter] = useSaveUrlFilter({
    ...defaultFilter,
    [offsetParams]: Number(offset) ?? 0,
  });

  const { total, requests, loading } = useAppointmentRequests(filter);

  const hasShowPaginate = useMemo(() => total > filter.limit, [total, filter]);

  const paginateSubtitle = useMemo(
    () =>
      `Showing ${+filter.offset + 1}-${Math.min(
        filter.offset + filter.limit,
        total,
      )} Appointments from ${total}`,
    [total, filter],
  );

  const setNewOffset = (offset: number) =>
    setFilter((vars) => ({ ...vars, [offsetParams]: offset }));

  if (loading && !requests.length) {
    return <Loader />;
  }

  if (!requests.length) {
    return null;
  }

  return (
    <CardWrapper
      testId="upcoming-appointments-card"
      title="My requests"
      alwaysExpanded
      rightComponentWrap={false}
      rightComponent={
        <div className="desktop:hidden">
          <Counter total={total} />
        </div>
      }
      subtitle={
        <div className="hidden desktop:block">
          <Counter total={total} />
        </div>
      }
      className="relative"
      titleClassName="text-xl desktop:text-h3 text-neutral-800  font-semibold"
    >
      <Loader
        className={twMerge(
          'absolute top-1/2 left-1/2 -translate-x-1/2 translate-y-1/2',
          !loading && 'hidden',
        )}
      />
      <div
        className={twMerge(
          'flex flex-col mt-4 w-full gap-4',
          loading && 'opacity-50',
        )}
      >
        {requests.map((req) => (
          <Card
            {...req}
            patient={req.patient as GetPatientOutput}
            key={req.id}
          />
        ))}
      </div>

      {hasShowPaginate && !loading && (
        <div className="flex w-full max-desktop:flex-col gap-4 justify-between items-center mt-7">
          <p className="text-sm font-medium text-med-gray-3">
            {paginateSubtitle}
          </p>
          <PaginateBlock
            itemsPerPage={filter.limit}
            totalItems={total}
            setNewOffset={setNewOffset}
            offsetParams={offsetParams}
          />
        </div>
      )}
    </CardWrapper>
  );
};

export default AppointmentRequests;
